import { formatSupabaseError, supabase } from '../../supabase';

export function isResendSmtp(smtp = {}) {
  const host = String(smtp.host || '').toLowerCase();
  const pass = String(smtp.password || '');
  return host.includes('resend.com') || pass.startsWith('re_');
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildBodies({ subject, note, message }) {
  const custom = String(message || '').trim();
  if (custom) {
    const title = subject?.trim() || 'Message from Gym CRM';
    const safe = escapeHtml(custom).replace(/\n/g, '<br/>');
    return {
      subject: title,
      text: custom,
      html: `<div style="font-family:Arial,Helvetica,sans-serif;line-height:1.55;color:#18181b;font-size:14px">
        <p style="margin:0">${safe}</p>
      </div>`,
    };
  }

  const title = subject || 'Gym CRM — manual email check';
  const extra = note ? `\n\nNote: ${note}` : '';
  const text =
    `This is a manual email check from Gym CRM Notifications.\n`
    + `If you received this, delivery through your connected SMTP is working.`
    + extra
    + `\n\nCheck Inbox and Spam / Promotions.`;
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;line-height:1.5;color:#18181b">
    <h2 style="margin:0 0 12px;font-size:18px">${escapeHtml(title)}</h2>
    <p style="margin:0 0 12px">This is a <strong>manual email check</strong> from Gym CRM Notifications.</p>
    <p style="margin:0 0 12px">If you received this, delivery through your connected SMTP is working.</p>
    ${note ? `<p style="margin:0 0 12px"><em>Note:</em> ${escapeHtml(note)}</p>` : ''}
    <p style="margin:0;font-size:13px;color:#71717a">Also check Spam / Promotions if it is not in Inbox.</p>
  </div>`;
  return { subject: title, text, html };
}

async function readFunctionError(error, data) {
  if (data?.error) return String(data.error);

  const ctx = error?.context;
  if (ctx) {
    try {
      if (typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return String(body.error);
        if (typeof body?.message === 'string') return body.message;
        if (typeof body === 'string') return body;
        return JSON.stringify(body);
      }
      if (typeof ctx.text === 'function') {
        const text = await ctx.text();
        if (text) return text;
      }
    } catch {
      /* ignore parse failure */
    }
  }

  return error?.message || formatSupabaseError(error, 'Could not call email function.');
}

async function invokeFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });

  if (error) {
    const detail = await readFunctionError(error, data);
    if (/failed to send a request|fetch|network/i.test(detail)) {
      throw new Error(
        `${detail} Deploy the Edge Function once: npx supabase functions deploy ${name}`,
      );
    }
    if (/not found|404|FunctionsRelayError/i.test(detail)) {
      throw new Error(
        `Edge Function "${name}" is not deployed. Run: npx supabase functions deploy ${name}`,
      );
    }
    throw new Error(detail);
  }

  if (data && data.ok === false) {
    throw new Error(String(data.error || 'Email send failed.'));
  }

  return data || { ok: true };
}

function sanitizeSmtpSecret(value) {
  return String(value || '')
    // App passwords are often pasted with spaces; Google expects no spaces
    .replace(/\s+/g, '')
    // Strip zero-width / BOM chars from copy-paste
    .replace(/[\u200B-\u200D\uFEFF]/g, '');
}

/**
 * Sends email via Supabase Edge Function using Connect Email SMTP / Resend settings.
 */
export async function sendNotificationTestEmail({
  smtp,
  to,
  subject = '',
  note = '',
  message = '',
}) {
  const toAddr = String(to || '').trim();
  if (!toAddr) {
    throw new Error('Enter a recipient email address.');
  }

  const host = String(smtp?.host || '').trim();
  const fromEmail = String(smtp?.fromEmail || '').trim();
  const username = String(smtp?.username || '').trim();
  const password = sanitizeSmtpSecret(smtp?.password);

  if (!host || !fromEmail) {
    throw new Error('Connect Email first (SMTP host and From email are required).');
  }
  if (!username || !password) {
    throw new Error('SMTP username and password are required. Open Manage Email and save them again.');
  }

  const bodies = buildBodies({
    subject: subject.trim(),
    note: note.trim(),
    message: message.trim(),
  });

  if (isResendSmtp({ ...smtp, password })) {
    return invokeFunction('send-resend-email', {
      apiKey: password,
      fromEmail,
      fromName: smtp.fromName || 'Gym CRM',
      to: toAddr,
      subject: bodies.subject,
      text: bodies.text,
      html: bodies.html,
    });
  }

  return invokeFunction('send-test-email', {
    smtp: {
      host,
      port: smtp.port || '587',
      encryption: smtp.encryption || 'tls',
      username,
      password,
      fromName: smtp.fromName || 'Gym CRM',
      fromEmail,
    },
    to: toAddr,
    subject: bodies.subject,
    text: bodies.text,
    html: bodies.html,
  });
}
